import { useEffect, useState } from 'react';
import { getCitas, createCita, updateEstatusCita, getClientes, getProspectos, getCandidatos, createProspecto } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/ui/Modal';

/* ── types ──────────────────────────────────────────────────── */
const ESTATUS_COLORS: Record<string, string> = {
  Programada: 'bg-primary-100 text-primary-700',
  Completada: 'bg-green-100 text-green-700',
  NoShow:     'bg-red-100 text-red-700',
  Cancelada:  'bg-gray-100 text-gray-500',
};

// Daniel Santos pidió explícitamente diferenciar por color las citas de
// clientes/prospectos (ventas) de las de referidos/candidatos (reclutamiento)
// — no por tipo de cita — para que la agenda muestre todo junto sin
// confundirlas ("para no confundirlos, podemos ponerle un color a cada
// recuadro... de los que son citas de clientes y las que son citas de
// referidos").
const CATEGORIA_COLORS: Record<'ventas' | 'reclutamiento', string> = {
  ventas:        'bg-blue-500',
  reclutamiento: 'bg-orange-500',
};
const CATEGORIA_LABELS: Record<'ventas' | 'reclutamiento', string> = {
  ventas:        'Clientes / Prospectos',
  reclutamiento: 'Referidos (Reclutamiento)',
};
const getCategoria = (c: any): 'ventas' | 'reclutamiento' => c.candidatoId ? 'reclutamiento' : 'ventas';

const TIPOS = ['PrimerContacto','Seguimiento','Cierre','Renovacion','Otro','Personal'];
const TIPO_LABELS: Record<string, string> = {
  PrimerContacto: 'Primer contacto',
  Seguimiento:    'Seguimiento',
  Cierre:         'Cita de Cierre',
  Renovacion:     'Renovación',
  Siniestro:      'Siniestro',
  Otro:           'Otro',
  Personal:       'Personal',
};

const DAYS   = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const EMPTY_FORM = {
  titulo:'', fechaHora:'', duracionMinutos:'60',
  tipo:'Seguimiento', notas:'', lugar:'',
  clienteId:'', prospectoId:'', candidatoId:'',
  todoElDia: false, frecuenciaRecurrencia: '', fechaFinRecurrencia: '',
};

const EMPTY_NUEVO_PROSPECTO = { nombre: '', telefono: '' };

const FRECUENCIAS = ['Diaria', 'Semanal', 'Mensual'];

/* ── helpers ─────────────────────────────────────────────────── */
const daysInMonth = (year: number, month: number) =>
  new Date(year, month + 1, 0).getDate();
const firstWeekday = (year: number, month: number) =>
  new Date(year, month, 1).getDay();
const toDateKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

/* ── component ───────────────────────────────────────────────── */
export default function Citas() {
  const { user } = useAuth();
  const canAccessReclutamiento = user?.rol === 'Admin' || user?.rol === 'SuperAdmin' || user?.rol === 'Gerente';

  const now = new Date();
  // El tab ya NO filtra qué se ve en la agenda (antes ocultaba un tipo de
  // cita mientras ves el otro, dando una falsa sensación de disponibilidad
  // libre); solo decide qué campo de vínculo mostrar en el formulario de
  // "Nueva cita".
  const [formTab, setFormTab]   = useState<'prospectos' | 'reclutamiento'>('prospectos');
  const [view, setView]         = useState<'calendar' | 'list'>('calendar');
  const [calYear, setCalYear]   = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [citas, setCitas]       = useState<any[]>([]);
  const [clientes, setClientes]     = useState<any[]>([]);
  const [prospectos, setProspectos] = useState<any[]>([]);
  const [candidatos, setCandidatos] = useState<any[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [nuevoProspecto, setNuevoProspecto] = useState(false);
  const [nuevoProspectoForm, setNuevoProspectoForm] = useState(EMPTY_NUEVO_PROSPECTO);

  const load = () => getCitas().then(r => setCitas(Array.isArray(r.data) ? r.data : []));

  useEffect(() => {
    load();
    getClientes().then(r => setClientes(Array.isArray(r.data) ? r.data : []));
    getProspectos().then(r => setProspectos(Array.isArray(r.data) ? r.data : []));
    if (canAccessReclutamiento)
      getCandidatos().then(r => setCandidatos(Array.isArray(r.data) ? r.data : []));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* group by date — siempre todas las citas juntas, sin ocultar ninguna */
  const byDate: Record<string, any[]> = {};
  citas.forEach(c => {
    const key = toDateKey(new Date(c.fechaHora));
    (byDate[key] = byDate[key] || []).push(c);
  });

  const handleEstatus = async (id: number, estatus: string) => {
    await updateEstatusCita(id, estatus);
    setCitas(prev => prev.map(c => c.id === id ? { ...c, estatus } : c));
  };

  const openNew = (dateStr?: string) => {
    const f = { ...EMPTY_FORM };
    if (dateStr) f.fechaHora = `${dateStr}T09:00`;
    setForm(f);
    setError('');
    setNuevoProspecto(false);
    setNuevoProspectoForm(EMPTY_NUEVO_PROSPECTO);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let prospectoId = form.prospectoId ? Number(form.prospectoId) : null;

      if (nuevoProspecto && nuevoProspectoForm.nombre.trim()) {
        const res = await createProspecto({
          nombre: nuevoProspectoForm.nombre.trim(),
          telefono: nuevoProspectoForm.telefono.trim(),
        });
        prospectoId = res.data.id;
        setProspectos(prev => [...prev, res.data]);
      }

      await createCita({
        titulo: form.titulo,
        fechaHora: form.fechaHora,
        duracionMinutos: Number(form.duracionMinutos) || 60,
        tipo: form.tipo,
        notas: form.notas || null,
        lugar: form.lugar || null,
        clienteId: form.clienteId ? Number(form.clienteId) : null,
        prospectoId,
        candidatoId: form.candidatoId ? Number(form.candidatoId) : null,
        todoElDia: form.todoElDia,
        frecuenciaRecurrencia: form.frecuenciaRecurrencia || null,
        fechaFinRecurrencia: form.frecuenciaRecurrencia ? (form.fechaFinRecurrencia || null) : null,
      });
      setShowModal(false);
      load();
    } catch { setError('Error al guardar.'); }
    finally { setSaving(false); }
  };

  const field = (key: Exclude<keyof typeof EMPTY_FORM, 'todoElDia'>) => ({
    value: form[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [key]: e.target.value })),
  });

  const contactoNombre = (c: any) =>
    c.candidato?.nombre || c.cliente?.nombre || c.prospecto?.nombre || '—';

  /* ── calendar rendering ────────────────────────────────────── */
  const totalDays = daysInMonth(calYear, calMonth);
  const startDay  = firstWeekday(calYear, calMonth);
  const todayKey  = toDateKey(now);
  const days: (number | null)[] = [
    ...Array(startDay).fill(null),
    ...Array.from({ length: totalDays }, (_, i) => i + 1),
  ];
  while (days.length % 7 !== 0) days.push(null);

  const citasSelectedDay = selected ? (byDate[selected] || []) : [];

  /* ── render ──────────────────────────────────────────────────── */
  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          {/* View toggle */}
          <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <button
              onClick={() => setView('calendar')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${view === 'calendar' ? 'bg-gray-100 text-gray-800' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              📅 Calendario
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${view === 'list' ? 'bg-gray-100 text-gray-800' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              ☰ Lista
            </button>
          </div>
          {view === 'calendar' && (
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-1.5 shadow-sm">
              <button onClick={() => { let m = calMonth - 1; let y = calYear; if (m < 0) { m = 11; y--; } setCalMonth(m); setCalYear(y); setSelected(null); }}
                className="text-gray-500 hover:text-primary-600 px-1">‹</button>
              <span className="text-sm font-semibold text-gray-700 w-36 text-center">
                {MONTHS[calMonth]} {calYear}
              </span>
              <button onClick={() => { let m = calMonth + 1; let y = calYear; if (m > 11) { m = 0; y++; } setCalMonth(m); setCalYear(y); setSelected(null); }}
                className="text-gray-500 hover:text-primary-600 px-1">›</button>
            </div>
          )}
        </div>
        <button
          onClick={() => openNew()}
          className="bg-primary-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-primary-700 shadow-sm"
        >
          + Nueva cita
        </button>
      </div>

      {/* ── CALENDAR VIEW ───────────────────────────────────────── */}
      {view === 'calendar' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Month grid */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-7 border-b border-gray-100">
              {DAYS.map(d => (
                <div key={d} className={`py-2 text-center text-xs font-semibold ${d === 'Dom' || d === 'Sáb' ? 'text-gray-300' : 'text-gray-500'}`}>
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {days.map((day, idx) => {
                const key = day
                  ? `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
                  : null;
                const dayCitas = key ? (byDate[key] || []) : [];
                const isToday = key === todayKey;
                const isSelected = key === selected;
                const isWeekend = idx % 7 === 0 || idx % 7 === 6;

                return (
                  <div
                    key={idx}
                    onClick={() => { if (key) setSelected(isSelected ? null : key); }}
                    className={`min-h-[80px] p-1.5 border-b border-r border-gray-50 cursor-pointer transition-colors
                      ${!day ? 'bg-gray-50/50' : ''}
                      ${isWeekend && day ? 'bg-gray-50/40' : ''}
                      ${isSelected ? 'bg-primary-50 border-primary-200' : day ? 'hover:bg-gray-50' : ''}
                    `}
                  >
                    {day && (
                      <>
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium mb-1
                          ${isToday ? 'bg-primary-600 text-white' : isSelected ? 'text-primary-700 font-bold' : 'text-gray-700'}
                        `}>
                          {day}
                        </div>
                        <div className="space-y-0.5">
                          {dayCitas.slice(0, 3).map((c: any) => (
                            <div
                              key={c.id}
                              className={`text-[10px] leading-tight text-white px-1.5 py-0.5 rounded truncate ${CATEGORIA_COLORS[getCategoria(c)]}`}
                            >
                              {new Date(c.fechaHora).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })} {c.titulo}
                            </div>
                          ))}
                          {dayCitas.length > 3 && (
                            <div className="text-[10px] text-gray-400 px-1">+{dayCitas.length - 3} más</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Side panel */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Categoría</p>
              <div className="space-y-2">
                {(Object.keys(CATEGORIA_LABELS) as Array<keyof typeof CATEGORIA_LABELS>).map(key => (
                  <div key={key} className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${CATEGORIA_COLORS[key]}`} />
                    <span className="text-xs text-gray-600">{CATEGORIA_LABELS[key]}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-400">{citas.length} citas en total</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              {!selected ? (
                <p className="text-sm text-gray-400 text-center py-4">Selecciona un día para ver las citas</p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-700">
                      {new Date(selected + 'T12:00').toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                    <button onClick={() => openNew(selected)} className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                      + Agregar
                    </button>
                  </div>
                  {citasSelectedDay.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-400 mb-2">Sin citas</p>
                      <button onClick={() => openNew(selected)}
                        className="text-xs bg-primary-50 text-primary-600 px-3 py-1.5 rounded-lg hover:bg-primary-100">
                        + Agendar cita
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {citasSelectedDay.sort((a, b) =>
                        new Date(a.fechaHora).getTime() - new Date(b.fechaHora).getTime()
                      ).map((c: any) => (
                        <div key={c.id} className={`border-l-4 pl-3 py-1 rounded-r ${CATEGORIA_COLORS[getCategoria(c)].replace('bg-','border-l-')}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{c.titulo}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(c.fechaHora).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                {c.duracionMinutos && ` · ${c.duracionMinutos}min`}
                                {c.lugar && ` · ${c.lugar}`}
                              </p>
                              <p className="text-xs text-gray-400">{contactoNombre(c)}</p>
                            </div>
                            <select
                              value={c.estatus}
                              onChange={e => handleEstatus(c.id, e.target.value)}
                              className={`text-[10px] px-1.5 py-0.5 rounded-full border-0 font-medium flex-shrink-0 ${ESTATUS_COLORS[c.estatus]}`}
                            >
                              {Object.keys(ESTATUS_COLORS).map(s => <option key={s}>{s}</option>)}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Próximas citas</p>
              <div className="space-y-2">
                {citas
                  .filter(c => new Date(c.fechaHora) >= now && c.estatus === 'Programada')
                  .sort((a, b) => new Date(a.fechaHora).getTime() - new Date(b.fechaHora).getTime())
                  .slice(0, 5)
                  .map(c => (
                    <div key={c.id} className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${CATEGORIA_COLORS[getCategoria(c)]}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-700 truncate">{c.titulo}</p>
                        <p className="text-[10px] text-gray-400">
                          {new Date(c.fechaHora).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                          {' '}{new Date(c.fechaHora).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                {citas.filter(c => new Date(c.fechaHora) >= now && c.estatus === 'Programada').length === 0 && (
                  <p className="text-xs text-gray-400">Sin próximas citas</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── LIST VIEW ────────────────────────────────────────────── */}
      {view === 'list' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Título</th>
                <th className="px-4 py-3 text-left">Fecha y hora</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Contacto</th>
                <th className="px-4 py-3 text-left">Lugar</th>
                <th className="px-4 py-3 text-left">Agente</th>
                <th className="px-4 py-3 text-left">Estatus</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...citas].sort((a,b) => new Date(b.fechaHora).getTime() - new Date(a.fechaHora).getTime()).map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${CATEGORIA_COLORS[getCategoria(c)]}`} />
                      {c.titulo}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {new Date(c.fechaHora).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{TIPO_LABELS[c.tipo] ?? c.tipo}</td>
                  <td className="px-4 py-3 text-gray-600">{contactoNombre(c)}</td>
                  <td className="px-4 py-3 text-gray-600">{c.lugar || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.agente?.nombre}</td>
                  <td className="px-4 py-3">
                    <select value={c.estatus} onChange={e => handleEstatus(c.id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${ESTATUS_COLORS[c.estatus]}`}>
                      {Object.keys(ESTATUS_COLORS).map(s => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {citas.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">No hay citas</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── NEW CITA MODAL ───────────────────────────────────────── */}
      {showModal && (
        <Modal title="Nueva cita" onClose={() => setShowModal(false)} size="md">
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
              <input type="text" {...field('titulo')} required
                placeholder="Presentación de propuesta, Entrevista inicial..."
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha y hora *</label>
              <input type="datetime-local" {...field('fechaHora')} required
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duración (min)</label>
              <input type="number" {...field('duracionMinutos')} min={15} step={15}
                disabled={form.todoElDia}
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm disabled:bg-gray-100" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="todoElDia" checked={form.todoElDia}
                onChange={e => setForm(f => ({ ...f, todoElDia: e.target.checked }))}
                className="h-4 w-4" />
              <label htmlFor="todoElDia" className="text-sm font-medium text-gray-700">Dura todo el día</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Repetir</label>
              <select {...field('frecuenciaRecurrencia')} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm">
                <option value="">No se repite</option>
                {FRECUENCIAS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            {form.frecuenciaRecurrencia && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Repetir hasta</label>
                <input type="date" {...field('fechaFinRecurrencia')} required
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
              <select {...field('tipo')} required className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm">
                {TIPOS.map(t => <option key={t} value={t}>{TIPO_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lugar</label>
              <input type="text" {...field('lugar')} placeholder="Oficina, Zoom, domicilio..."
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" />
            </div>

            {/* Vínculo: a quién se le agenda la cita */}
            {canAccessReclutamiento && (
              <div className="col-span-2 flex bg-gray-100 rounded-lg p-0.5 w-fit">
                <button type="button" onClick={() => setFormTab('prospectos')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${formTab === 'prospectos' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'}`}>
                  Cliente / Prospecto
                </button>
                <button type="button" onClick={() => setFormTab('reclutamiento')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${formTab === 'reclutamiento' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500'}`}>
                  Candidato (Reclutamiento)
                </button>
              </div>
            )}
            {formTab === 'reclutamiento' ? (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Candidato</label>
                <select {...field('candidatoId')} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm">
                  <option value="">— Ninguno —</option>
                  {candidatos.map((c: any) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                  <select {...field('clienteId')} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm">
                    <option value="">— Ninguno —</option>
                    {clientes.map((c: any) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Prospecto</label>
                    <button
                      type="button"
                      onClick={() => {
                        setNuevoProspecto(v => !v);
                        setForm(f => ({ ...f, prospectoId: '' }));
                        setNuevoProspectoForm(EMPTY_NUEVO_PROSPECTO);
                      }}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      {nuevoProspecto ? 'Elegir existente' : '+ Nuevo prospecto'}
                    </button>
                  </div>
                  {nuevoProspecto ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Nombre del prospecto *"
                        value={nuevoProspectoForm.nombre}
                        onChange={e => setNuevoProspectoForm(f => ({ ...f, nombre: e.target.value }))}
                        required
                        className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="text"
                        placeholder="Teléfono"
                        value={nuevoProspectoForm.telefono}
                        onChange={e => setNuevoProspectoForm(f => ({ ...f, telefono: e.target.value }))}
                        className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  ) : (
                    <select {...field('prospectoId')} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm">
                      <option value="">— Ninguno —</option>
                      {prospectos.map((p: any) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                  )}
                </div>
              </>
            )}

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea {...field('notas')} rows={2} placeholder="Temas a tratar, preparación necesaria..."
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm resize-none" />
            </div>
            {error && <p className="col-span-2 text-red-500 text-sm">{error}</p>}
            <div className="col-span-2 flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Guardando...' : 'Agendar cita'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
